import { useEffect, useState } from "react";
import { Layout } from "../components/Layout";
import { Panel, Stat } from "../components/Panel";
import {
  fetchRuntimeDepartments,
  inviteDepartmentContributor,
  removeDepartmentContributor,
  replaceDepartmentContributor,
  resendDepartmentContributorSetupLink,
  updateDepartmentContributorEmail,
  type ContributorInviteResult,
  type RuntimeWorkspaceOverview
} from "../lib/api/runtimeWorkspaceClient";
import { contributorLifecycleLabel, getContributorLifecycleStatus } from "../lib/contributorLifecycle";
import { statusLabel } from "../lib/departmentWorkflow";
import { useWorkspaceLanguage, withLanguagePrefix } from "../lib/i18n";
import { readBilingualText } from "../lib/i18n/bilingualContent";
import { buildInviteLink } from "../lib/invite";
import {
  canInviteExecutivePerspective,
  dedupeExecutivePerspectives,
  getPerspectiveDisplayName,
  getPerspectiveTypeLabel,
  isBillablePerspective
} from "../lib/valueWorkflow";
import type { PerspectiveType } from "../types";

export function Departments() {
  const language = useWorkspaceLanguage();
  const copy = language === "en" ? {
    eyebrow: "Departments",
    title: "Create and manage departments",
    description: "This means departments or central functions such as Executive Management, sales, marketing, finance, delivery, customer success or operations.",
    loadError: "Departments could not be loaded.",
    inviteError: "Contributor could not be invited.",
    setupError: "Setup link could not be created.",
    emailError: "Contributor email could not be changed.",
    replaceError: "Contributor could not be replaced.",
    removeError: "Contributor could not be removed.",
    loadingTitle: "Departments are loading",
    loadingBody: "Workspace data is loading...",
    loadErrorTitle: "Departments could not be loaded",
    noWorkspaceTitle: "No workspace available yet.",
    noWorkspaceBody: "There is currently no workspace stored for your access.",
    addDepartment: "Add department",
    limitReached: "Department limit reached",
    totalDepartments: "Departments total",
    submitted: "Submitted",
    valueRound: "Value Round",
    limitBody: "The department limit for this workspace has been reached.",
    countTitle: (count: number, max: number) => `${count} of ${max} departments created`,
    emptyStrong: "No departments created yet.",
    emptyBody: "Create the first department as soon as the workspace is ready for it.",
    noContributor: "No contributor invited yet.",
    contributorStatus: "Contributor status",
    inProgress: "In progress",
    noResponses: "No answers submitted yet.",
    valueSubmitted: "Submitted",
    valueDraft: "In progress",
    valueOpen: "Open",
    inviteLink: "Invite link",
    copyLink: "Copy link",
    viewAnswers: "View answers",
    passwordStatus: "Password status",
    active: "Active",
    invited: "Invited",
    resendSetup: "Send setup link again",
    correctEmail: "Correct email",
    changeEmail: "Change email",
    existingAnswers: "Answers already exist for this department. Replacing the contributor keeps the history.",
    replaceContributor: "Replace contributor",
    removeContributor: "Remove contributor",
    contributorEmail: "Contributor email",
    replacementEmailPlaceholder: "new.person@company.com",
    contributorEmailPlaceholder: "name@company.com",
    inviteContributor: "Invite contributor",
    setupMailSent: "Setup email was sent.",
    setupLinkCreated: "Setup link was created.",
    setupValidUntil: "Setup link valid until",
    copySetupLink: "Copy setup link",
    inviteExecutive: "Invite Executive Management Perspective",
    executiveExists: "Executive Management perspective already exists.",
    inviteFinance: "Invite Finance Perspective",
    openValueRound: "Open Value Round",
    viewValueContribution: "View Value Round Contribution",
    viewInitialAnswers: "View answers",
    openWeighting: "Open weighting",
    functionalPerspective: "Functional Perspective",
    organizationalOnly: "Organizational responsibility only",
    votingPerspective: "Voting Perspective",
    perspectiveLabels: {
      department: "Department",
      executive: "Executive Management",
      finance: "Finance / Controlling",
      commercial: "Commercial / Revenue",
      operations: "Operations / Delivery",
      other: "Additional Perspective"
    } satisfies Record<PerspectiveType, string>
  } : {
    eyebrow: "Abteilungen",
    title: "Abteilungen anlegen und verwalten",
    description: "Gemeint sind Abteilungen oder zentrale Funktionen wie Geschäftsführung, Sales, Marketing, Finance, Delivery, Customer Success oder Operations.",
    loadError: "Abteilungen konnten nicht geladen werden.",
    inviteError: "Contributor konnte nicht eingeladen werden.",
    setupError: "Setup-Link konnte nicht erzeugt werden.",
    emailError: "Contributor-E-Mail konnte nicht geändert werden.",
    replaceError: "Contributor konnte nicht ersetzt werden.",
    removeError: "Contributor konnte nicht entfernt werden.",
    loadingTitle: "Abteilungen werden geladen",
    loadingBody: "Workspace-Daten werden geladen...",
    loadErrorTitle: "Abteilungen konnten nicht geladen werden",
    noWorkspaceTitle: "Noch kein Workspace vorhanden.",
    noWorkspaceBody: "Für Ihren Zugang ist aktuell noch kein Workspace hinterlegt.",
    addDepartment: "Abteilung hinzufügen",
    limitReached: "Abteilungslimit erreicht",
    totalDepartments: "Abteilungen gesamt",
    submitted: "Eingereicht",
    valueRound: "Value-Runde",
    limitBody: "Das Abteilungslimit dieses Workspace ist erreicht.",
    countTitle: (count: number, max: number) => `${count} von ${max} Abteilungen angelegt`,
    emptyStrong: "Noch keine Abteilungen angelegt.",
    emptyBody: "Legen Sie eine erste Abteilung an, sobald der Workspace dafür bereit ist.",
    noContributor: "Noch kein Contributor eingeladen.",
    contributorStatus: "Contributor-Status",
    inProgress: "In Bearbeitung",
    noResponses: "Noch keine Antworten eingereicht.",
    valueSubmitted: "Eingereicht",
    valueDraft: "In Bearbeitung",
    valueOpen: "Offen",
    inviteLink: "Invite-Link",
    copyLink: "Link kopieren",
    viewAnswers: "Antworten ansehen",
    passwordStatus: "Passwortstatus",
    active: "Aktiv",
    invited: "Eingeladen",
    resendSetup: "Setup-Link erneut senden",
    correctEmail: "E-Mail korrigieren",
    changeEmail: "E-Mail ändern",
    existingAnswers: "Für diese Abteilung existieren bereits Antworten. Beim Ersetzen bleibt die Historie erhalten.",
    replaceContributor: "Contributor ersetzen",
    removeContributor: "Contributor entfernen",
    contributorEmail: "Contributor E-Mail",
    replacementEmailPlaceholder: "neue.person@unternehmen.de",
    contributorEmailPlaceholder: "name@unternehmen.de",
    inviteContributor: "Contributor einladen",
    setupMailSent: "Setup-Mail wurde gesendet.",
    setupLinkCreated: "Setup-Link wurde erzeugt.",
    setupValidUntil: "Setup-Link gültig bis",
    copySetupLink: "Setup-Link kopieren",
    inviteExecutive: "Geschäftsleitungsperspektive einladen",
    executiveExists: "Geschäftsleitungsperspektive ist bereits vorhanden.",
    inviteFinance: "Finance-Perspektive einladen",
    openValueRound: "Value-Runde öffnen",
    viewValueContribution: "Value-Beitrag ansehen",
    viewInitialAnswers: "Antworten ansehen",
    openWeighting: "Gewichtung öffnen",
    functionalPerspective: "Fachliche Perspektive",
    organizationalOnly: "Nur organisatorische Verantwortung",
    votingPerspective: "Stimmberechtigte Perspektive",
    perspectiveLabels: {
      department: "Abteilung",
      executive: "Geschäftsleitung",
      finance: "Finance / Controlling",
      commercial: "Commercial / Revenue",
      operations: "Operations / Delivery",
      other: "Weitere Perspektive"
    } satisfies Record<PerspectiveType, string>
  };
  const [overview, setOverview] = useState<RuntimeWorkspaceOverview>({ departments: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [inviteEmails, setInviteEmails] = useState<Record<string, string>>({});
  const [inviteResults, setInviteResults] = useState<Record<string, ContributorInviteResult>>({});
  const [inviteErrors, setInviteErrors] = useState<Record<string, string>>({});
  const [correctionEmails, setCorrectionEmails] = useState<Record<string, string>>({});
  const [replacementEmails, setReplacementEmails] = useState<Record<string, string>>({});
  const [pendingDepartmentId, setPendingDepartmentId] = useState<string | undefined>();

  useEffect(() => {
    let isMounted = true;

    async function loadDepartments() {
      setIsLoading(true);
      setError(undefined);
      try {
        const nextOverview = await fetchRuntimeDepartments();
        if (isMounted) setOverview(nextOverview);
      } catch (caught) {
        if (isMounted) setError(caught instanceof Error ? caught.message : copy.loadError);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    void loadDepartments();

    return () => {
      isMounted = false;
    };
  }, [copy.loadError]);

  const workspace = overview.workspace;
  const departments = dedupeExecutivePerspectives(overview.departments);
  const submittedCount = departments.filter(
    (department) => department.status === "submitted" || department.status === "reviewed" || department.responseStatus === "submitted"
  ).length;
  const valueSubmittedCount = departments.filter((department) => department.valueInputStatus === "submitted").length;
  const billableDepartments = departments.filter(isBillablePerspective);
  const canAddDepartment = Boolean(workspace && billableDepartments.length < workspace.maxDepartments);
  const canAddExecutivePerspective = Boolean(workspace && canInviteExecutivePerspective(overview.departments, overview.ownerPerspective));
  const ownerExecutiveDepartmentId = overview.ownerPerspective?.perspectiveType === "executive" ? overview.ownerPerspective.id : undefined;

  function copyInviteLink(inviteToken: string) {
    void navigator.clipboard?.writeText(buildInviteLink(window.location.origin, inviteToken));
  }

  function copySetupLink(setupLink: string) {
    void navigator.clipboard?.writeText(setupLink);
  }

  function updateDepartment(result: ContributorInviteResult) {
    setOverview((current) => ({
      ...current,
      departments: current.departments.map((department) =>
        department.id === result.department.id ? result.department : department
      )
    }));
    setInviteResults((current) => ({ ...current, [result.department.id]: result }));
    setInviteErrors((current) => {
      const next = { ...current };
      delete next[result.department.id];
      return next;
    });
  }

  async function inviteContributor(departmentId: string) {
    setPendingDepartmentId(departmentId);
    try {
      const result = await inviteDepartmentContributor(departmentId, inviteEmails[departmentId] ?? "");
      updateDepartment(result);
      setInviteEmails((current) => ({ ...current, [departmentId]: "" }));
    } catch (caught) {
      setInviteErrors((current) => ({
        ...current,
        [departmentId]: caught instanceof Error ? caught.message : copy.inviteError
      }));
    } finally {
      setPendingDepartmentId(undefined);
    }
  }

  async function resendSetupLink(departmentId: string) {
    setPendingDepartmentId(departmentId);
    try {
      const result = await resendDepartmentContributorSetupLink(departmentId);
      updateDepartment(result);
    } catch (caught) {
      setInviteErrors((current) => ({
        ...current,
        [departmentId]: caught instanceof Error ? caught.message : copy.setupError
      }));
    } finally {
      setPendingDepartmentId(undefined);
    }
  }

  async function correctContributorEmail(departmentId: string) {
    setPendingDepartmentId(departmentId);
    try {
      const result = await updateDepartmentContributorEmail(departmentId, correctionEmails[departmentId] ?? "");
      updateDepartment(result);
      setCorrectionEmails((current) => ({ ...current, [departmentId]: "" }));
    } catch (caught) {
      setInviteErrors((current) => ({
        ...current,
        [departmentId]: caught instanceof Error ? caught.message : copy.emailError
      }));
    } finally {
      setPendingDepartmentId(undefined);
    }
  }

  async function replaceContributor(departmentId: string) {
    setPendingDepartmentId(departmentId);
    try {
      const result = await replaceDepartmentContributor(departmentId, replacementEmails[departmentId] ?? "");
      updateDepartment(result);
      setReplacementEmails((current) => ({ ...current, [departmentId]: "" }));
    } catch (caught) {
      setInviteErrors((current) => ({
        ...current,
        [departmentId]: caught instanceof Error ? caught.message : copy.replaceError
      }));
    } finally {
      setPendingDepartmentId(undefined);
    }
  }

  async function removeContributor(departmentId: string) {
    setPendingDepartmentId(departmentId);
    try {
      const result = await removeDepartmentContributor(departmentId);
      setOverview((current) => ({
        ...current,
        departments: current.departments.map((department) =>
          department.id === result.department.id ? result.department : department
        )
      }));
      setInviteResults((current) => {
        const next = { ...current };
        delete next[departmentId];
        return next;
      });
      setInviteErrors((current) => {
        const next = { ...current };
        delete next[departmentId];
        return next;
      });
    } catch (caught) {
      setInviteErrors((current) => ({
        ...current,
        [departmentId]: caught instanceof Error ? caught.message : copy.removeError
      }));
    } finally {
      setPendingDepartmentId(undefined);
    }
  }

  return (
    <Layout
      eyebrow={copy.eyebrow}
      title={copy.title}
      description={copy.description}
    >
      {isLoading ? <Panel title={copy.loadingTitle}><p>{copy.loadingBody}</p></Panel> : null}
      {error ? (
        <Panel title={copy.loadErrorTitle} variant="accent">
          <p className="form-error" role="alert">{error}</p>
        </Panel>
      ) : null}
      {!isLoading && !error && !workspace ? (
        <Panel title={copy.noWorkspaceTitle} variant="accent">
          <p>{copy.noWorkspaceBody}</p>
        </Panel>
      ) : null}

      {workspace ? (
        <>
          <div className="toolbar">
            {workspace ? (
              <>
                {canAddDepartment ? (
                <a className="button button-primary" href={withLanguagePrefix("/workspace/departments/new", language)}>
                  {copy.addDepartment}
                </a>
                ) : null}
                {canAddExecutivePerspective ? (
                <a className="button button-secondary" href={withLanguagePrefix("/workspace/departments/new?perspective=executive", language)}>
                  {copy.inviteExecutive}
                </a>
                ) : workspace ? <span className="form-note">{copy.executiveExists}</span> : null}
                {canAddDepartment ? (
                <a className="button button-secondary" href={withLanguagePrefix("/workspace/departments/new?perspective=finance", language)}>
                  {copy.inviteFinance}
                </a>
                ) : null}
              </>
            ) : (
              <button className="button button-secondary" type="button" disabled>
                {copy.limitReached}
              </button>
            )}
          </div>

          <div className="grid grid-3">
            <Stat label={copy.totalDepartments} value={`${billableDepartments.length}/${workspace.maxDepartments}`} />
            <Stat label={copy.submitted} value={String(submittedCount)} />
            <Stat label={copy.valueRound} value={`${valueSubmittedCount}/${departments.length}`} />
          </div>

          {!canAddDepartment ? (
            <Panel title={copy.limitReached} variant="accent">
              <p>{copy.limitBody}</p>
            </Panel>
          ) : null}

          <Panel title={copy.countTitle(billableDepartments.length, workspace.maxDepartments)}>
            {departments.length === 0 ? (
              <div className="empty-state">
                <p><strong>{copy.emptyStrong}</strong></p>
                <p>{copy.emptyBody}</p>
              </div>
            ) : (
              <div className="owner-department-cards">
                {departments.map((department) => {
                  const hasResponse = Boolean(department.responseStatus);
                  const isOwnerExecutive = ownerExecutiveDepartmentId === department.id;
                  const lifecycleStatus = getContributorLifecycleStatus(department);
                  const responseLabel = hasResponse ? copy.inProgress : copy.noResponses;
                  return (
                    <article className="owner-department-card" key={department.id}>
                      <div className="owner-department-card__header">
                        <div>
                          <strong>{getPerspectiveDisplayName({ ...department, name: readBilingualText(department.nameI18n ?? department.name, language) }, language)}</strong>
                          <p>{copy.functionalPerspective}: {getPerspectiveTypeLabel(department.perspectiveType, language)}</p>
                        </div>
                        <span className={`status-badge status-${department.status}`}>{statusLabel(department.status as "invited" | "started" | "submitted" | "reviewed", language)}</span>
                      </div>
                      <div className="owner-department-card__meta">
                        <span>{department.isVotingPerspective === false ? copy.organizationalOnly : copy.votingPerspective}</span>
                        <span>{department.participantEmail ?? copy.noContributor}</span>
                        <span>{copy.contributorStatus}: <strong>{contributorLifecycleLabel(lifecycleStatus, language)}</strong></span>
                        {department.responseStatus !== "submitted" ? <span>{responseLabel}</span> : null}
                        <span>{copy.valueRound}: <strong>{department.valueInputStatus === "submitted" ? copy.valueSubmitted : department.valueInputStatus === "draft" ? copy.valueDraft : copy.valueOpen}</strong></span>
                      </div>
                      <div className="owner-department-card__actions">
                      {isOwnerExecutive ? (
                        <>
                          <a className="button button-secondary" href={withLanguagePrefix(`/workspace/departments/${department.id}/input`, language)}>
                            {copy.viewInitialAnswers}
                          </a>
                          <a className="button button-primary owner-value-round-button" href={withLanguagePrefix(`/workspace/departments/${department.id}/input#value-round`, language)}>
                            {department.valueInputStatus === "submitted" ? copy.viewValueContribution : copy.openValueRound}
                          </a>
                          <a className="button button-secondary" href={withLanguagePrefix(`/workspace/departments/${department.id}/weighting`, language)}>
                            {copy.openWeighting}
                          </a>
                        </>
                      ) : (
                        <>
                          <a href={buildInviteLink(window.location.origin, department.inviteToken)}>{copy.inviteLink}</a>
                          <button className="button button-secondary" type="button" onClick={() => copyInviteLink(department.inviteToken)}>
                            {copy.copyLink}
                          </button>
                          {hasResponse ? (
                            <a className="button button-secondary" href={withLanguagePrefix(`/workspace/departments/${department.id}/responses`, language)}>
                              {copy.viewAnswers}
                            </a>
                          ) : (
                            <button className="button button-secondary" type="button" disabled>
                              {copy.viewAnswers}
                            </button>
                          )}
                          {department.participantUserId ? (
                            <a className="button button-secondary" href={withLanguagePrefix(`/workspace/departments/${department.id}/weighting`, language)}>
                              {copy.openWeighting}
                            </a>
                          ) : null}
                        </>
                      )}
                      </div>
                      {!isOwnerExecutive ? (
                      <div className="owner-department-card__form form form-grid">
                        {department.participantEmail ? (
                          <>
                            <span>
                              {copy.passwordStatus}: <strong>{department.participantHasPassword ? copy.active : copy.invited}</strong>
                            </span>
                            <button
                              className="button button-secondary"
                              type="button"
                              onClick={() => resendSetupLink(department.id)}
                              disabled={pendingDepartmentId === department.id}
                            >
                              {copy.resendSetup}
                            </button>
                            {!department.participantHasPassword ? (
                              <>
                                <label>
                                  {copy.correctEmail}
                                  <input
                                    type="email"
                                    placeholder={department.participantEmail}
                                    value={correctionEmails[department.id] ?? ""}
                                    onChange={(event) =>
                                      setCorrectionEmails((current) => ({ ...current, [department.id]: event.target.value }))
                                    }
                                  />
                                </label>
                                <button
                                  className="button button-secondary"
                                  type="button"
                                  onClick={() => correctContributorEmail(department.id)}
                                  disabled={pendingDepartmentId === department.id}
                                >
                                  {copy.changeEmail}
                                </button>
                              </>
                            ) : null}
                            {department.responseStatus ? (
                              <p className="form-note form-wide">
                                {copy.existingAnswers}
                              </p>
                            ) : null}
                            <label>
                              {copy.replaceContributor}
                              <input
                                type="email"
                                placeholder={copy.replacementEmailPlaceholder}
                                value={replacementEmails[department.id] ?? ""}
                                onChange={(event) =>
                                  setReplacementEmails((current) => ({ ...current, [department.id]: event.target.value }))
                                }
                              />
                            </label>
                            <button
                              className="button button-secondary"
                              type="button"
                              onClick={() => replaceContributor(department.id)}
                              disabled={pendingDepartmentId === department.id}
                            >
                              {copy.replaceContributor}
                            </button>
                            <button
                              className="button button-secondary"
                              type="button"
                              onClick={() => removeContributor(department.id)}
                              disabled={pendingDepartmentId === department.id}
                            >
                              {copy.removeContributor}
                            </button>
                          </>
                        ) : (
                          <>
                            <label>
                              {copy.contributorEmail}
                              <input
                                type="email"
                                placeholder={copy.contributorEmailPlaceholder}
                                value={inviteEmails[department.id] ?? ""}
                                onChange={(event) =>
                                  setInviteEmails((current) => ({ ...current, [department.id]: event.target.value }))
                                }
                              />
                            </label>
                            <button
                              className="button button-primary"
                              type="button"
                              onClick={() => inviteContributor(department.id)}
                              disabled={pendingDepartmentId === department.id}
                            >
                              {copy.inviteContributor}
                            </button>
                          </>
                        )}
                        {inviteResults[department.id] ? (
                          <div className="form-wide">
                            <p className="form-note">
                              {inviteResults[department.id].emailSent
                                ? copy.setupMailSent
                                : (inviteResults[department.id].emailError ?? copy.setupLinkCreated)}
                            </p>
                            <p className="form-note">
                              {copy.setupValidUntil} {new Date(inviteResults[department.id].expiresAt).toLocaleString(language === "en" ? "en-US" : "de-DE")}
                            </p>
                            <button
                              className="button button-secondary"
                              type="button"
                              onClick={() => copySetupLink(inviteResults[department.id].setupLink)}
                            >
                              {copy.copySetupLink}
                            </button>
                          </div>
                        ) : null}
                        {inviteErrors[department.id] ? (
                          <p className="form-error form-wide" role="alert">{inviteErrors[department.id]}</p>
                        ) : null}
                      </div>
                      ) : null}
                    </article>
                  );
                })}
              </div>
            )}
          </Panel>
        </>
      ) : null}
    </Layout>
  );
}
